import React, { useMemo } from "react";
import Dot from "./Dot";
import { tr } from "../i18n";
import { resolveBlockRows, resolveFieldValue } from "../utils/apartmentFields";

// Constants
const HOVER_BOX_WIDTH = 250;
const POSITION_OFFSET = 30;
const EDGE_MARGIN = 8;

// Utility functions
const calculatePosition = (mousePosition, isRtl) => {
  const position = {};

  // Vertical positioning
  if (mousePosition.y < window.innerHeight / 2) {
    position.top = mousePosition.y;
  } else {
    position.bottom = window.innerHeight - mousePosition.y;
  }

  // Horizontal positioning
  if (!isRtl) {
    position.left = mousePosition.x + POSITION_OFFSET;
  } else {
    position.left = mousePosition.x - HOVER_BOX_WIDTH - POSITION_OFFSET;
  }

  // Clamp so the box never renders partially off-screen (e.g. hovering an
  // apartment near the left edge in RTL previously pushed it mostly outside
  // the viewport, where the embedding iframe's overflow:hidden clipped it).
  const maxLeft = window.innerWidth - HOVER_BOX_WIDTH - EDGE_MARGIN;
  position.left = Math.min(Math.max(position.left, EDGE_MARGIN), Math.max(maxLeft, EDGE_MARGIN));

  return position;
};

const getApartmentStatus = (apartment, statuses) => {
  return apartment && statuses.find((status) => 
    status && status.status_name === apartment.available
  );
};

// Sub-components
const StatusHeader = ({ status, isRtl }) => (
  <div
    className="header"
    style={{
      display: "flex",
      alignItems: "start",
    }}
  >
    {status && (
      <Dot
        cx={3}
        cy={3}
        radius={3}
        fill={status.status_color}
        style={{
          width: ".5rem",
          height: ".5rem",
          marginTop: "5px",
        }}
      />
    )}
    <span
      style={{
        marginRight: ".5rem",
        marginLeft: ".5rem",
      }}
    >
      {status?.status_name}
    </span>
  </div>
);

const ApartmentDetails = ({ apartmentBlockFieldsOrder, apartment }) => {
  const rows = useMemo(
    () => resolveBlockRows(apartment, apartmentBlockFieldsOrder),
    [apartment, apartmentBlockFieldsOrder]
  );
  return (
    <div className="detailsList">
      <ul>
        {rows.map((row) => (
          <li key={row.key}>
            <div className="d-flex mb-1">
              <span>
                {row.title} {row.value}
              </span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

const FloorDisplay = ({ apartment, isRtl }) => (
  <div 
    className="floor"
    style={{
      position: 'absolute',
      top: '5px',
      left: !isRtl ? 'auto' : '1.5rem',
      right: !isRtl ? '1.5rem' : 'auto',
    }}
  >
    <span
      style={{
        marginRight: ".5rem",
        marginLeft: ".5rem",
      }}
    >
      {tr("floor")}
    </span>
    <div
      style={{
        fontSize: 36,
      }}
    >
      {resolveFieldValue(apartment, "product_acf_floor_name") ??
        resolveFieldValue(apartment, "product_acf_floor")}
    </div>
  </div>
);

const TourButton = ({ apartment, goToApartmentPage, colors }) => (
  <div
    className="roundButton"
    onClick={() => goToApartmentPage(apartment)}
    style={{
      border: `1px solid ${colors.primary}`,
    }}
  >
    <span>{tr("tour")}</span>
  </div>
);

const FloorViewButton = ({ apartment, onFloorView, colors }) => (
  <div
    className="roundButton"
    onClick={() => onFloorView(apartment)}
    style={{
      border: `1px solid ${colors.primary}`,
    }}
  >
    <span>{tr("floorView")}</span>
  </div>
);

const HoverApp = ({
  apartment,
  mousePosition,
  goToApartmentPage,
  onFloorView,
  isRtl,
  colors,
  statuses,
  apartmentBlockFieldsOrder,
}) => {
  const status = useMemo(() => 
    getApartmentStatus(apartment, statuses), 
    [apartment, statuses]
  );

  const containerStyle = useMemo(() => {
    const baseStyle = {
      position: "absolute",
      direction: isRtl ? "rtl" : "ltr",
      backgroundColor: "rgba(0, 0, 0, 0.6)",
    };

    if (apartment && mousePosition) {
      return {
        ...baseStyle,
        ...calculatePosition(mousePosition, isRtl),
      };
    }

    return baseStyle;
  }, [apartment, mousePosition, isRtl]);

  const ContentComponent = () => (
    <>
      <StatusHeader status={status} isRtl={isRtl} />
      
      <div
        className="mt-1"
        style={{
          fontSize: 12,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <ApartmentDetails 
          apartmentBlockFieldsOrder={apartmentBlockFieldsOrder} 
          apartment={apartment} 
        />
        
        <div className="">
          <FloorDisplay apartment={apartment} isRtl={isRtl} />
          {apartment.floor_id && (
            <FloorViewButton
              apartment={apartment}
              onFloorView={onFloorView}
              colors={colors}
              isRtl={isRtl}
            />
          )}
          <TourButton
            apartment={apartment}
            goToApartmentPage={goToApartmentPage}
            colors={colors}
            isRtl={isRtl}
          />
        </div>
      </div>
    </>
  );

  return (
    <div
      className={`apartmentBox rounded shadowed ${apartment ? "active" : ""}`}
      style={containerStyle}
    >
      {apartment && <ContentComponent />}
    </div>
  );
};

export default HoverApp;
